import type {
  GranularidadeDeRelatorio,
  RelatorioDeAns,
  RelatorioDeBacklog,
  RelatorioDeFcr,
  RelatorioDeMix,
  RelatorioDeReabertura,
  RelatorioDeResolucao,
  RelatorioDeVolume,
} from '@central/contracts';
import { get } from '@/lib/api';

/**
 * Acesso ao `ReportsController` (`/reports`).
 *
 * Uma função por rota do servidor, sem nenhuma inventada — o que não existe no
 * controller não existe aqui, e a tela que precisaria disso mostra a lacuna em
 * vez de simular o dado. Os tipos vêm de `@central/contracts`: a API e a tela
 * leem a mesma declaração, e um campo que mude de forma quebra a compilação em
 * vez de virar `undefined` no gráfico.
 */

const RAIZ = '/reports';

export type TipoDeChamado = 'INCIDENT' | 'REQUEST' | 'PROBLEM' | 'CHANGE' | 'TASK';

export interface FiltroDeRelatorio {
  de: string;
  ate: string;
  granularidade?: GranularidadeDeRelatorio;
  organizationId?: string;
  teamId?: string;
  tipo?: readonly TipoDeChamado[];
}

function comConsulta(caminho: string, filtro: FiltroDeRelatorio): string {
  const busca = new URLSearchParams();
  busca.set('de', filtro.de);
  busca.set('ate', filtro.ate);
  if (filtro.granularidade) busca.set('granularidade', filtro.granularidade);
  if (filtro.organizationId) busca.set('organizationId', filtro.organizationId);
  if (filtro.teamId) busca.set('teamId', filtro.teamId);
  // Array vira parâmetro repetido — o formato que o `@Transform` do DTO espera.
  for (const tipo of filtro.tipo ?? []) busca.append('tipo', tipo);
  return `${caminho}?${busca.toString()}`;
}

export const obterVolume = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeVolume>(comConsulta(`${RAIZ}/volume`, filtro));

export const obterAns = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeAns>(comConsulta(`${RAIZ}/ans`, filtro));

export const obterResolucao = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeResolucao>(comConsulta(`${RAIZ}/resolucao`, filtro));

export const obterBacklog = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeBacklog>(comConsulta(`${RAIZ}/backlog`, filtro));

export const obterReabertura = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeReabertura>(comConsulta(`${RAIZ}/reabertura`, filtro));

export const obterMix = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeMix>(comConsulta(`${RAIZ}/mix`, filtro));

export const obterFcr = (filtro: FiltroDeRelatorio) =>
  get<RelatorioDeFcr>(comConsulta(`${RAIZ}/fcr`, filtro));

/**
 * Chaves do TanStack Query.
 *
 * O filtro inteiro entra na chave: trocar de equipe é outro dado, não o mesmo
 * dado atualizado. Sem isso, voltar para o filtro anterior mostraria o número
 * da consulta nova por um instante — e um número errado piscando num relatório
 * é pior do que um esqueleto de carregamento.
 */
export const chavesDeRelatorio = {
  raiz: ['relatorios'] as const,
  de: (aba: string, filtro: FiltroDeRelatorio) => ['relatorios', aba, filtro] as const,
};
